<i18n>
{
  "en": {
    "terminal": {
      "serialnumber": "Serial number",
      "hardware": "Part number",
      "location": "Location"
    },
    "titles": {
      "system": "System",
      "country": "Select country",
      "footer": "Select country or use previous configuration if one exists"
    },
    "buttons": {
      "back": "Back",
      "continue": "Continue",
      "setup": "Setup",
      "quit": "Quit",
      "ok": "OK",
      "usePreviousConfig": "Use previous configuration",
      "advancedConfiguration": "Advanced Configuration"
    }
  },
  "da": {
    "terminal": {
      "serialnumber": "Serienummer",
      "hardware": "Partnummer",
      "location": "Lokation"
    },
    "titles": {
      "system": "System",
      "country": "Vælg land",
      "footer": "Vælg land eller benyt tidligere konfiguration, hvis en findes"
    },
    "buttons": {
      "back": "Tilbage",
      "continue": "Fortsæt",
      "quit": "Afslut",
      "ok": "OK",
      "usePreviousConfig": "Benyt tidligere konfiguration",
      "advancedConfiguration": "Avanceret Konfiguration"
    }
  }
}
</i18n>
<template>
  <layout>
    <b-container>
      <loading :active.sync="isLoading" 
        :can-cancel="false" 
        :is-full-page="true"></loading>

      <b-row>
        <b-col lg="8" offset-lg="2"> 
          <system-details :system="system" type="config" :compact="true"/>
          <!-- Header End -->

          <b-row>
            <div id="system-footer">
              <h6>{{ $t('titles.footer') }}</h6>
            </div>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-form>
                <!-- Combobox -->
                <i>{{$t('titles.country')}}:</i>
                <v-select 
                  id="" 
                  v-model="country" 
                  :options="countries" 
                  class="pb-3 style-chooser" 
                  style="content: none"
                  :disabled='countrySelectDisabled'
                  v-on:input="countryChange"
                  >
                </v-select> 

                <b-form-checkbox v-model="usePreviousConfiguration" v-if="system.PreviousConfiguration"  @click.native.stop v-on:input="countryChange()">
                  {{$t('buttons.usePreviousConfig')}}
                </b-form-checkbox>


              </b-form>
            </b-col>
          </b-row>

          <b-row>
           <b-alert show dismissible variant="danger" v-for="error in errorMessages" v-bind:key="error.Message">{{error.Message}}</b-alert>
          </b-row>

          <b-row class="mt-4">
            <b-col>
                <b-button 
                  type="button" 
                  variant="outline-secondary" 
                  active-class="" 
                  class="min-width-button" 
                  :to="{ name: 'system-config-overview', params: { logosid: this.system.LogosID }}">
                    {{$t('buttons.back')}}
                </b-button>

                <b-button 
                  type="button" 
                  variant="outline-secondary" 
                  active-class="" 
                  class="min-width-button" 
                  :disabled="continueDisabled" 
                  @click="confirmIfPreviousConfigurationExits()"
                  
                  >
                    {{$t('buttons.continue')}}
                </b-button>

                <b-button 
                  type="button" 
                  align-self="end"
                  variant="outline-secondary" 
                  active-class="" 
                  class="min-width-button" 
                  style="float:right" 
                  :to="{ name: 'issue-advanced-system-config', params: { logosid: this.system.LogosID }, query: { country: this.country } }"
                  :disabled="continueDisabled">
                    {{$t('buttons.advancedConfiguration')}}
                </b-button>
            </b-col>
          </b-row>

        </b-col>
      </b-row>
    </b-container>

    <previous-configuration-exists-modal ref="previousConfigurtionExists" v-on:accept="generateLicense()">
    </previous-configuration-exists-modal>

    <send-config-by-mail-modal ref="complete" type="config" :email="email" :system="system" :file="files.license" v-on:accept="redirectToCreditOverview"  >
    </send-config-by-mail-modal>

  </layout>
</template>

<script>
import _ from 'lodash'
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.min.css';
import vSelect from 'vue-select'

import PreviousConfigurationExistsModal from './modals/PreviousConfigurationExistsModal'
import SendConfigByMailModal from './modals/SendConfigByMailModal'

import SystemDetails from './subcomponent/SystemDetails'

import Layout from './Layout'
import elg from '../api/elg.gateway.js'
import jwt from '../utils/jwt.js'

export default {
  components: {
    Layout,
    Loading,
    vSelect,

    PreviousConfigurationExistsModal,
    SendConfigByMailModal,

    SystemDetails
  },
  data() {
    return {
      isLoading: false,
      system: {},
      files: {
        license: undefined,
        credit: undefined
      },
      sendLicenseFileOnMail: false,

      configuration: {},
      usePreviousConfiguration: false,
      country: undefined,
      countries: [],

      frax: {
          frax1550: {
            enabled: false,
            selected: false,
            model: {}
          },
          frax1940: {
            enabled: false,
            selected: false,
            model: {}
          }
      },

      errorMessages: [],

      sendLicenseFileOnMail: false
    }
  },
  methods:{
    confirmIfPreviousConfigurationExits(){
      if(this.system.PreviousConfiguration){
        this.$refs['previousConfigurtionExists'].show();
      }
      else{
        this.generateLicense();
      }
    },
    generateLicense(){
      this.isLoading = true;   
      var data = {};

      try{
        var regulatoryAuthority = _.find(this.configuration.Misc, {Name: "RegulatoryAuthority"});
        data = {
          LogosID: this.system.LogosID,
          IssueInformation: {
            LicenseType: 'License',
            LicenseActionType: 'Replace existing'
          },
          SystemInformation: {
            SerialNumber: this.system.SerialNumber,
            InstallationId: this.system.InstallationID,
            SystemPartNumber: this.system.SystemPartNumber,
            SystemDongle: this.system.SystemDongle,
            RegulatoryAuthority: regulatoryAuthority.Value
          },
          Applicators: _.clone(this.configuration.Applicators),
          Applications: _.clone(this.configuration.Applications),
          SystemSoftwareModules: _.clone(this.configuration.SystemSoftwareModules),
        }
      }
      catch(err){
        console.error(err);
      }

      elg()
      .post('/api/v1/license?saveConfiguration=true', data)
      .then( res => {
        this.isLoading = false;
        this.files.license = res.data;
        this.$refs.complete.show();
      })
      .catch( err => {
        this.isLoading = false;
        this.errorMessages.push(err.response.data);
        for( var i = 0; i < err.response.data.Errors.length; i++){
          this.errorMessages.push(err.response.data.Errors[i]);
        }

      });
    },
    confirm(){
      if(this.sendLicenseFileOnMail){
        var data = {
          Recipient: this.email,
          Subject: "New Ellipse license file",
          Body: "A new Ellipse license file has been generated."
        };
        
        elg()
        .post(`/api/v1/files/${this.files.license.FileID}/mail`, data)
        .then( res => {
          this.$refs.complete.hide();
        });
      } else {
        this.$refs.complete.hide();
      }
    },
    countryChange(test){
      var query = "";
      if(this.usePreviousConfiguration !== true){
        query = `?country=${this.country}`
      }

      elg().get(`/api/v1/systems/${this.$route.params.logosid}/configuration${query}`)
      .then( res => {
        this.configuration = res.data;
      });
    },
    redirectToCreditOverview() {
      this.$router.push({
        name: 'system-config-overview',
        params:{
          logosid: this.$route.params.logosid
        }
      })
    }
  },
  computed: {
    countrySelectDisabled(){
      return this.usePreviousConfiguration === true
    },
    continueDisabled(){
      return ( this.country === undefined || this.country === null ) && ( this.usePreviousConfiguration === false );
    },
    email(){
      return jwt.getEmail();
    },
    /*regulatoryAuthority(){
      if(this.usePreviousConfiguration === true){
        var param = _.find(this.configuration.Misc, { 'Name': 'RegulatoryAuthority'});
        if(param === undefined)
          return 'CE';

        return param.Value;
      }
      else
      {
        if(this.country === 'USA' || this.country === 'Canada')
          return 'FDA';
  
        return 'CE';
      }
    }*/
  },
  mounted() {
    elg()
    .get(`/api/v1/systems/${this.$route.params.logosid}`)
    .then( res => {
      this.system = res.data;
      var systemType = this.$t(`systemtypes.${this.system.SystemPartNumber}`); // I'm so sorry...

      return elg()
        .get(`/api/v1/templates/systemtype/${systemType}/countries`);

    })
    .then( res => {
      this.countries = _.map(res.data, function(c){
        return c.Name;
      });
    })
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// WebApp/components/IssueSystemConfig.vue